import { motion } from "framer-motion";
import { Particles } from "@/components/landing/Particles";
import { AppStoreButton, GooglePlayButton } from "@/components/landing/StoreButtons";
import { Users, Route as RouteIcon, Radio } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const Stat = ({ icon: Icon, value, label, dotColor = "#39FF14", testId }) => (
  <motion.div
    initial={{ y: 12, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.6, ease, delay: 0.5 }}
    data-testid={testId}
    className="inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-black/35 backdrop-blur-xl px-4 py-3 shadow-[0_18px_60px_rgba(0,0,0,0.45)]"
  >
    <span className="relative inline-flex w-2 h-2">
      <span className="absolute inset-0 rounded-full pulse-ring" style={{ background: dotColor }} />
      <span className="relative inline-block w-2 h-2 rounded-full" style={{ background: dotColor }} />
    </span>
    <Icon className="w-4 h-4 text-white/80" />
    <div className="flex flex-col leading-tight">
      <span className="font-display font-semibold text-white text-base">{value}</span>
      <span className="text-[11px] text-white/60 uppercase tracking-wider font-mono">{label}</span>
    </div>
  </motion.div>
);

export const Hero = () => {
  const { t } = useLanguage();
  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1558981806-ec527fa84c39?auto=format&fit=crop&w=2400&q=80"
          alt="Cinematic motorcycle on open road"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07090B]/95 via-[#07090B]/65 to-[#07090B]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#07090B]" />
        <div className="absolute inset-0 hero-backdrop opacity-60 mix-blend-screen" />
      </div>

      <Particles />

      <div className="absolute top-0 left-0 right-0 h-32 section-edge-glow pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-24 lg:pt-44 lg:pb-32">
        <motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease }}
          className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 backdrop-blur-md px-3 py-1.5"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] shadow-[0_0_12px_rgba(57,255,20,0.7)]" />
          <span className="text-[11px] text-white/80 font-mono uppercase tracking-[0.18em]">{t.hero.pill}</span>
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={{ y: 18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease, delay: 0.08 }}
          className="font-display font-bold text-white mt-6 text-[40px] leading-[0.95] sm:text-6xl lg:text-[88px] tracking-tight max-w-4xl"
        >
          {t.hero.ride} <span className="text-white">{t.hero.discover}</span>{" "}
          <span className="text-[#39FF14] drop-shadow-[0_0_24px_rgba(57,255,20,0.45)]">{t.hero.connect}</span>
        </motion.h1>

        <motion.p
          data-testid="hero-subhead"
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, ease, delay: 0.2 }}
          className="mt-5 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed"
        >
          {t.hero.subhead}
        </motion.p>

        <motion.div
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, ease, delay: 0.3 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <AppStoreButton variant="primary" />
          <GooglePlayButton variant="glass" />
        </motion.div>

        <div className="mt-14 flex flex-wrap gap-3 sm:gap-4">
          <Stat icon={Users} value="1600+" label={t.hero.statRiders} dotColor="#39FF14" testId="hero-stat-riders-connected" />
          <Stat icon={RouteIcon} value="310" label={t.hero.statRoutes} dotColor="#7DD3FC" testId="hero-stat-routes-shared" />
          <Stat icon={Radio} value="24" label={t.hero.statLive} dotColor="#39FF14" testId="hero-stat-live-rides" />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/55"
        aria-hidden="true"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.25em]">{t.hero.scroll}</span>
        <span className="w-px h-10 bg-gradient-to-b from-[#39FF14]/70 to-transparent" />
      </motion.div>
    </section>
  );
};
